// useCollaborationFilter — Progressive filtering for Collaboration quadrants
// Task #1868 — User Story #1863
//
// Filtering chain:
// 1. Org alphabet filter → filter orgs by name initial
// 2. Org selection → filter contacts via contactOrgLinks
// 3. Contact alphabet filter → further filter contacts by surname initial
// 4. Contact selection → filter todos and links via contactIds
// 5. No selection → all items shown
//
// Contacts sorted by surname, orgs sorted alphabetically.

import { createContext, useContext, useState, useMemo, useCallback, type ReactNode } from 'react'
import { createElement } from 'react'
import type {
  CollaborationContact, CollaborationOrg, CollaborationLink,
  CollaborationTodo, CollaborationFilterState,
} from '../types/collaboration'
import type { CrmContactOrg } from '../types'

interface CollaborationFilterProviderProps {
  contacts: CollaborationContact[]
  orgs: CollaborationOrg[]
  links: CollaborationLink[]
  todos: CollaborationTodo[]
  contactOrgLinks: CrmContactOrg[]
  children: ReactNode
}

const CollaborationFilterContext = createContext<CollaborationFilterState | null>(null)

/**
 * Extract surname (last word) from a full name string.
 *
 * BUG: `CollaborationContact.name`/`CollaborationOrg.name` are typed as a
 * required `string`, but the normaliser feeding this hook (useCollaborationData)
 * can only guarantee that when the source CRM record's name field actually
 * decrypted — a real project ("ZTA 2.5") has CRM rows whose name field the
 * backend fail-closed on, and any future producer of this shape could make
 * the same mistake. `name.trim()` / `name.toLowerCase()` on an undefined
 * value crashed the entire Research Network tab with "Cannot read properties
 * of undefined (reading 'toLowerCase')". Tolerate a missing/blank name here
 * too, defense-in-depth, so a bad name never white-screens the tab again.
 */
function extractSurname(name: string | undefined): string {
  const safe = (name || '').trim()
  if (!safe) return ''
  const parts = safe.split(/\s+/)
  return parts.length > 1 ? parts[parts.length - 1] : parts[0]
}

/** Sort contacts by surname, then full name. */
function sortBySurname(contacts: CollaborationContact[]): CollaborationContact[] {
  return [...contacts].sort((a, b) => {
    const surnameA = extractSurname(a.name).toLowerCase()
    const surnameB = extractSurname(b.name).toLowerCase()
    if (surnameA !== surnameB) return surnameA.localeCompare(surnameB)
    return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
  })
}

export function CollaborationFilterProvider({
  contacts, orgs, links, todos, contactOrgLinks, children,
}: CollaborationFilterProviderProps) {
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<string>>(new Set())
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set())
  const [contactAlphabetFilter, setContactAlphabetFilter] = useState<Set<string>>(new Set())
  const [orgAlphabetFilter, setOrgAlphabetFilter] = useState<Set<string>>(new Set())

  // Build org→contacts lookup from contactOrgLinks
  const orgToContactIds = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const link of contactOrgLinks) {
      if (!map.has(link.org_id)) map.set(link.org_id, new Set())
      map.get(link.org_id)!.add(link.contact_id)
    }
    return map
  }, [contactOrgLinks])

  // === ORG FILTERING ===
  // Sort orgs alphabetically. `(o.name || '')` guards against an undefined
  // name reaching here (see extractSurname's comment above) — this crashed
  // the whole tab in production.
  const sortedOrgs = useMemo(() => {
    return [...orgs].sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))
  }, [orgs])

  // Available letters for org alphabet filter (from all orgs)
  const orgAvailableLetters = useMemo(() => {
    const letters = new Set<string>()
    for (const o of sortedOrgs) {
      const first = (o.name || '').trim()[0]
      if (first) letters.add(first.toUpperCase())
    }
    return letters
  }, [sortedOrgs])

  // Apply org alphabet filter
  const filteredOrgs = useMemo(() => {
    if (orgAlphabetFilter.size === 0) return sortedOrgs
    return sortedOrgs.filter(o => {
      const first = (o.name || '').trim()[0]
      return first && orgAlphabetFilter.has(first.toUpperCase())
    })
  }, [sortedOrgs, orgAlphabetFilter])

  // === CONTACT FILTERING ===
  // Step 1: Filter contacts by org selection
  const orgFilteredContacts = useMemo(() => {
    if (selectedOrgIds.size === 0) return contacts
    const allowedContactIds = new Set<string>()
    for (const orgId of selectedOrgIds) {
      const contactIds = orgToContactIds.get(orgId)
      if (contactIds) {
        for (const cid of contactIds) allowedContactIds.add(cid)
      }
    }
    return contacts.filter(c => allowedContactIds.has(c.id))
  }, [contacts, selectedOrgIds, orgToContactIds])

  // Step 2: Available letters for contact alphabet filter (after org filter, before alphabet filter)
  const contactAvailableLetters = useMemo(() => {
    const letters = new Set<string>()
    for (const c of orgFilteredContacts) {
      const surname = extractSurname(c.name)
      if (surname) letters.add(surname[0].toUpperCase())
    }
    return letters
  }, [orgFilteredContacts])

  // Step 3: Apply contact alphabet filter + sort by surname
  const filteredContacts = useMemo(() => {
    let result = orgFilteredContacts
    if (contactAlphabetFilter.size > 0) {
      result = result.filter(c => {
        const surname = extractSurname(c.name)
        return surname && contactAlphabetFilter.has(surname[0].toUpperCase())
      })
    }
    return sortBySurname(result)
  }, [orgFilteredContacts, contactAlphabetFilter])

  // === LINK & TODO FILTERING ===
  // BUG-138: some links/todos legitimately carry NO contact/org association
  // metadata (e.g. journal links, or a general/document-scoped todo). Filtering
  // those out on any selection wrongly emptied the quadrants. An item with no
  // association ids is treated as "unscoped" and always kept; items that DO
  // carry ids are filtered against the current selection as before.
  const filteredLinks = useMemo(() => {
    if (selectedContactIds.size === 0 && selectedOrgIds.size === 0) return links
    return links.filter(l => {
      if (l.contactIds.length === 0 && l.orgIds.length === 0) return true
      if (selectedContactIds.size > 0) return l.contactIds.some(cid => selectedContactIds.has(cid))
      return l.orgIds.some(oid => selectedOrgIds.has(oid))
    })
  }, [links, selectedContactIds, selectedOrgIds])

  const filteredTodos = useMemo(() => {
    if (selectedContactIds.size === 0 && selectedOrgIds.size === 0) return todos
    return todos.filter(t => {
      if (t.contactIds.length === 0 && t.orgIds.length === 0) return true
      if (selectedContactIds.size > 0) return t.contactIds.some(cid => selectedContactIds.has(cid))
      return t.orgIds.some(oid => selectedOrgIds.has(oid))
    })
  }, [todos, selectedContactIds, selectedOrgIds])

  // === ACTIONS ===
  const toggleOrg = useCallback((orgId: string) => {
    setSelectedOrgIds(prev => {
      const next = new Set(prev)
      if (next.has(orgId)) next.delete(orgId)
      else next.add(orgId)

      if (next.size > 0) {
        const allowedContactIds = new Set<string>()
        for (const oid of next) {
          const cids = orgToContactIds.get(oid)
          if (cids) for (const cid of cids) allowedContactIds.add(cid)
        }
        setSelectedContactIds(prevContacts => {
          const nextContacts = new Set<string>()
          for (const cid of prevContacts) {
            if (allowedContactIds.has(cid)) nextContacts.add(cid)
          }
          return nextContacts
        })
      }

      return next
    })
  }, [orgToContactIds])

  const toggleContact = useCallback((contactId: string) => {
    setSelectedContactIds(prev => {
      const next = new Set(prev)
      if (next.has(contactId)) next.delete(contactId)
      else next.add(contactId)
      return next
    })
  }, [])

  const toggleContactLetter = useCallback((letter: string) => {
    setContactAlphabetFilter(prev => {
      const next = new Set(prev)
      if (next.has(letter)) next.delete(letter)
      else next.add(letter)
      return next
    })
  }, [])

  const clearContactAlphabet = useCallback(() => {
    setContactAlphabetFilter(new Set())
  }, [])

  const toggleOrgLetter = useCallback((letter: string) => {
    setOrgAlphabetFilter(prev => {
      const next = new Set(prev)
      if (next.has(letter)) next.delete(letter)
      else next.add(letter)
      return next
    })
  }, [])

  const clearOrgAlphabet = useCallback(() => {
    setOrgAlphabetFilter(new Set())
  }, [])

  const clearAll = useCallback(() => {
    setSelectedOrgIds(new Set())
    setSelectedContactIds(new Set())
    setContactAlphabetFilter(new Set())
    setOrgAlphabetFilter(new Set())
  }, [])

  const value: CollaborationFilterState = useMemo(() => ({
    selectedOrgIds,
    selectedContactIds,
    filteredContacts,
    filteredLinks,
    filteredTodos,
    orgs: filteredOrgs,
    toggleOrg,
    toggleContact,
    clearAll,
    contactAlphabetFilter,
    contactAvailableLetters,
    toggleContactLetter,
    clearContactAlphabet,
    orgAlphabetFilter,
    orgAvailableLetters,
    toggleOrgLetter,
    clearOrgAlphabet,
  }), [selectedOrgIds, selectedContactIds, filteredContacts, filteredLinks, filteredTodos,
       filteredOrgs, toggleOrg, toggleContact, clearAll,
       contactAlphabetFilter, contactAvailableLetters, toggleContactLetter, clearContactAlphabet,
       orgAlphabetFilter, orgAvailableLetters, toggleOrgLetter, clearOrgAlphabet])

  return createElement(CollaborationFilterContext.Provider, { value }, children)
}

export function useCollaborationFilter(): CollaborationFilterState {
  const context = useContext(CollaborationFilterContext)
  if (!context) {
    throw new Error('useCollaborationFilter must be used within a CollaborationFilterProvider')
  }
  return context
}
